import app from 'flarum/forum/app';
import Page from 'flarum/common/components/Page';
import IndexPage from 'flarum/forum/components/IndexPage';
import ItemList from 'flarum/common/utils/ItemList';
import listItems from 'flarum/common/helpers/listItems';
import LinkButton from 'flarum/common/components/LinkButton';
import extractText from 'flarum/common/utils/extractText';
import dynamicallyLoadLib from '../utils/dynamicallyLoadLib';
import CalendarState from '../states/CalendarState';
import SelectDropdown from 'flarum/common/components/SelectDropdown';
import CalendarHero from './CalendarHero';

export default class CalendarPage extends Page {
	oninit(vnode) {
		super.oninit(vnode);
		this.bodyClass = 'App--calendar';
		app.history.push('advevents', extractText(app.translator.trans('kemze-calendar.forum.button.landing')));
	}

	oncreate(vnode) {
		super.oncreate(vnode);
		app.setTitle(extractText(app.translator.trans('kemze-calendar.forum.button.landing')));
		this.renderCalendar();
	}

	view() {
		return (
			<div className="IndexPage">
			{CalendarHero.component()}
			<div className="container">
			<div className="sideNavContainer">
			<nav className="IndexPage-nav sideNav">
			<ul>{listItems(this.sidebarItems().toArray())}</ul>
			</nav>
			<div className="IndexPage-results sideNavOffset">
			<div id="calendar" />
			<div className="CalendarPage-ical">
			<a className="Button Button--default" href="/events.ics" target="_blank" rel="noopener noreferrer">
			<i className="fas fa-calendar-check Button-icon" />
			<span className="Button-label">
			{app.translator.trans('kemze-calendar.forum.button.ical')}
			</span>
			</a>
			</div>
			</div>
			</div>
			</div>
			</div>
		);
	}

	sidebarItems() {
		const items = IndexPage.prototype.sidebarItems();

		items.setContent(
			'nav',
			SelectDropdown.component(
				{
					buttonClassName: 'Button',
					className: 'App-titleControl',
				},
				this.navItems().toArray()
			)
		);

		return items;
	}

	navItems() {
		const items = IndexPage.prototype.navItems();

		items.add(
			'events',
			<LinkButton icon="fas fa-calendar-alt" href={app.route('advevents')}>
			{app.translator.trans('kemze-calendar.forum.button.landing')}
			</LinkButton>,
			75
		);

		return items;
	}

	async renderCalendar() {
		await dynamicallyLoadLib('fullcalendarCore');
		await dynamicallyLoadLib([
			'fullcalendarLocales',
			'fullcalendarDayGrid',
			'fullcalendarInteraction',
			'fullcalendarList',
		]);

		const calendarEl = document.getElementById('calendar');
		if (!calendarEl) return;

		const calendar = new FullCalendar.Calendar(calendarEl, {
			locale: app.translator.getLocale(),
			headerToolbar: {
				left:   'prev,next today',
				center: 'title',
				right:  'dayGridMonth,listUpcoming',
			},
			initialView: 'dayGridMonth',
			eventTimeFormat: {
				hour:   '2-digit',
				minute: '2-digit',
				hour12: false,
			},
			eventClick: (info) => {
				info.jsEvent.preventDefault();
				const url = info.event.extendedProps.discussionUrl;
				if (url) window.location.href = url;
			},
			events: (info, successCb, failureCb) => {
				app.request({
					method: 'GET',
					url: `${app.forum.attribute('apiUrl')}/events`,
					params: {
						start: info.start.toISOString(),
						end:   info.end.toISOString(),
					},
				}).then((response) => {
					const records = app.store.pushPayload(response);
					const fcEvents = (Array.isArray(records) ? records : [records])
						.filter((e) => e.is_scheduled())
						.map((e) => this.toFCEvent(e));
					successCb(fcEvents);
				}).catch(() => successCb([]));
			},
			buttonText: {
				today:        'Today',
				dayGridMonth: 'Month',
			},
			views: {
				listUpcoming: {
					type: 'list',
					duration: { months: 6 },
					visibleRange: () => ({
						start: new Date(),
						end: new Date(new Date().setMonth(new Date().getMonth() + 6)),
					}),
					buttonText: app.translator.trans('kemze-calendar.forum.button.list_view'),
				},
			},
		});

		calendar.render();
		this.calendar = calendar;
	}

	toFCEvent(event) {
		const color = this.getTypeColor(event.event_type());
		return {
			title:  event.title() || '(untitled)',
			start:  event.event_start(),
			end:    event.event_end(),
			color,
			classNames: event.is_deleted() ? ['fc-event--deleted'] : [],
			extendedProps: {
				discussionUrl: event.discussion_url(),
			},
		};
	}

	getTypeColor(typeName) {
		if (!typeName) return undefined;
		try {
			const raw   = app.forum.attribute('kemze-calendar.event_types') || '[]';
			const types = typeof raw === 'string' ? JSON.parse(raw) : raw;
			const found = types.find((t) => t.name === typeName);
			return found?.color || undefined;
		} catch {
			return undefined;
		}
	}
}
